import { useMemo } from "react";

import type { AgChartOptions } from "ag-charts-community";

import {
  AgChartPanel,
  resolveToken,
  useIsDark,
  type CinchColor,
} from "@/components/cinch/charts";
import { Card } from "@/components/ui/card";

import type { LandscapePoint } from "../api";

/** The Deal-Health-style landscape (jrae, 2026-08-19): x = business-flow
 *  health, y = pipe health (0-100 from detector.health_score), bubble size =
 *  companies at L1 / 30d volume at L2/L3, color = state. Big + bottom-left is
 *  the triage quadrant. At L3 cardinality callers pre-filter to top-N-plus-
 *  unhealthy; this component just draws what it's given. */

const STATE_TOKENS: Record<string, CinchColor> = {
  critical: "danger",
  dormant: "purple",
  alert: "warning",
  learning: "teal",
  no_timely_signal: "orange",
  active: "success",
};

/** Options builder follows the DS convention (buildAgOptions(ds, type,
 *  isDark)): the theme flag is a cache-buster so resolveToken re-reads the
 *  cascade after a light/dark flip — the canvas can't use CSS vars. */
function buildLandscapeOptions(points: LandscapePoint[], _isDark: boolean): AgChartOptions {
  const states = [...new Set(points.map((p) => p.state))];
  return {
    series: states.map((state) => ({
      type: "bubble" as const,
      title: state,
      data: points
        .filter((p) => p.state === state)
        .map((p) => ({
          business: p.business,
          pipe: p.pipe,
          size: Math.max(1, p.companies ?? p.volume_30d ?? 1),
          name: p.company_name ?? p.location_name ?? p.key_path,
          key_path: p.key_path,
          level: p.level,
        })),
      xKey: "business",
      yKey: "pipe",
      sizeKey: "size",
      labelKey: "name",
      fill: resolveToken(STATE_TOKENS[state] ?? "brand"),
      fillOpacity: 0.65,
      strokeOpacity: 0,
      size: 10,
      maxSize: 42,
      label: { enabled: false },
      tooltip: {
        renderer: (params: { datum: Record<string, unknown> }) => ({
          title: String(params.datum.name ?? ""),
          content: `business ${String(params.datum.business)} · pipe ${String(
            params.datum.pipe,
          )} · size ${Number(params.datum.size).toLocaleString()}`,
        }),
      },
    })),
    axes: [
      {
        type: "number" as const,
        position: "bottom" as const,
        title: { text: "Business flow health" },
        min: 0,
        max: 105,
      },
      {
        type: "number" as const,
        position: "left" as const,
        title: { text: "Pipe health" },
        min: 0,
        max: 105,
      },
    ],
    legend: { position: "bottom" as const },
  } as unknown as AgChartOptions;
}

export function HealthLandscape({
  points,
  onPick,
}: {
  points: LandscapePoint[];
  onPick?: (p: { key_path?: string; level?: string }) => void;
}) {
  const isDark = useIsDark();
  const options = useMemo<AgChartOptions>(
    () => buildLandscapeOptions(points, isDark),
    [points, isDark],
  );

  if (points.length === 0) {
    return (
      <Card className="p-4">
        <p className="text-sm text-muted-foreground">
          No entities profiled yet — the landscape fills in after the first nightly run.
        </p>
      </Card>
    );
  }
  return (
    <Card className="p-4">
      <div className="mb-2 flex items-baseline justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Health landscape
        </h2>
        <span className="text-xs text-muted-foreground">
          big + bottom-left = triage first
        </span>
      </div>
      <AgChartPanel
        options={options}
        className="h-72"
        onSeriesNodeClick={(datum) =>
          onPick?.({
            key_path: datum.key_path as string | undefined,
            level: datum.level as string | undefined,
          })
        }
      />
    </Card>
  );
}
